import {
  SafetyOutlined,
  DeleteOutlined,
  PlusOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  Table,
  Button,
  message,
  Modal,
  Card,
  Space,
  Form,
  Input,
  Tag,
  Checkbox,
  Divider,
  Typography,
  Collapse,
} from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../shared/api/AllApi";
import usePermissions from "../../../hooks/usePermissions";
import "../../../shared/styles/ListPages.css";

const { Text } = Typography;

const GetRoles = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const [roleForm] = Form.useForm();
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Permission assignment modal
  const [permModalOpen, setPermModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedPermIds, setSelectedPermIds] = useState([]);
  const [permSaving, setPermSaving] = useState(false);

  // Create/Edit role modal
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [editingRoleData, setEditingRoleData] = useState(null);
  const [roleSaving, setRoleSaving] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/roles");
      setRoles(res.data.roles || []);
    } catch {
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const res = await apiClient.get("/permissions");
      setAllPermissions(res.data.permissions || []);
    } catch {
      setAllPermissions([]);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, [fetchRoles, fetchPermissions]);

  // --- Create / Edit Role ---
  const openCreateModal = () => {
    setEditingRoleData(null);
    roleForm.resetFields();
    setRoleModalOpen(true);
  };

  const openEditModal = (role) => {
    setEditingRoleData(role);
    roleForm.setFieldsValue({
      name: role.name,
      display_name: role.display_name,
    });
    setRoleModalOpen(true);
  };

  const handleSaveRole = async () => {
    try {
      const values = await roleForm.validateFields();
      setRoleSaving(true);

      const payload = {
        name: values.name.toLowerCase().replace(/\s+/g, "_"),
        display_name: values.display_name,
      };

      if (editingRoleData) {
        await apiClient.put(`/roles/${editingRoleData.id}`, payload);
        message.success(t("roleUpdated"));
      } else {
        await apiClient.post("/roles", payload);
        message.success(t("roleCreated"));
      }

      setRoleModalOpen(false);
      setEditingRoleData(null);
      roleForm.resetFields();
      fetchRoles();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(t("roleSaveError"));
    } finally {
      setRoleSaving(false);
    }
  };

  // --- Delete Role ---
  const handleDeleteRole = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`/roles/${deleteTarget.id}`);
      message.success(t("roleDeleted"));
      setDeleteTarget(null);
      fetchRoles();
    } catch {
      message.error(t("roleDeleteError"));
    }
  };

  // --- Permission Assignment ---
  const openPermModal = (role) => {
    setEditingRole(role);
    const permIds = (role.permissions || []).map((p) => p.id);
    setSelectedPermIds(permIds);
    setPermModalOpen(true);
  };

  const handlePermSave = async () => {
    if (!editingRole) return;
    try {
      setPermSaving(true);
      await apiClient.put(`/roles/${editingRole.id}/permissions`, {
        permissions: selectedPermIds,
      });
      message.success(t("permissionsUpdated"));
      setPermModalOpen(false);
      fetchRoles();
    } catch {
      message.error(t("permissionsUpdateError"));
    } finally {
      setPermSaving(false);
    }
  };

  const togglePermission = (id) => {
    setSelectedPermIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleModule = (modulePerms) => {
    const moduleIds = modulePerms.map((p) => p.id);
    const allSelected = moduleIds.every((id) => selectedPermIds.includes(id));
    if (allSelected) {
      setSelectedPermIds((prev) => prev.filter((id) => !moduleIds.includes(id)));
    } else {
      setSelectedPermIds((prev) => [...new Set([...prev, ...moduleIds])]);
    }
  };

  const selectAll = () => setSelectedPermIds(allPermissions.map((p) => p.id));
  const deselectAll = () => setSelectedPermIds([]);

  // Group permissions
  const featurePerms = useMemo(
    () => allPermissions.filter((p) => p.type === "feature"),
    [allPermissions]
  );
  const dataPerms = useMemo(
    () => allPermissions.filter((p) => p.type === "data"),
    [allPermissions]
  );

  const groupByModule = (perms) => {
    const groups = {};
    perms.forEach((p) => {
      const mod = p.module || "other";
      if (!groups[mod]) groups[mod] = [];
      groups[mod].push(p);
    });
    return groups;
  };

  const featureGroups = useMemo(() => groupByModule(featurePerms), [featurePerms]);
  const dataGroups = useMemo(() => groupByModule(dataPerms), [dataPerms]);

  const renderPermissionGroup = (groups, title) => (
    <>
      <Text strong style={{ fontSize: 16, display: "block", marginBottom: 8 }}>
        {title}
      </Text>
      <Collapse
        accordion={false}
        defaultActiveKey={Object.keys(groups)}
        items={Object.entries(groups).map(([module, perms]) => {
          const moduleIds = perms.map((p) => p.id);
          const allChecked = moduleIds.every((id) => selectedPermIds.includes(id));
          const someChecked = moduleIds.some((id) => selectedPermIds.includes(id));

          return {
            key: module,
            label: (
              <Space>
                <Checkbox
                  checked={allChecked}
                  indeterminate={someChecked && !allChecked}
                  onChange={() => toggleModule(perms)}
                  onClick={(e) => e.stopPropagation()}
                />
                <span style={{ fontWeight: 600, textTransform: "capitalize" }}>
                  {t(module)}
                </span>
              </Space>
            ),
            children: (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {perms.map((p) => (
                  <Checkbox
                    key={p.id}
                    checked={selectedPermIds.includes(p.id)}
                    onChange={() => togglePermission(p.id)}
                  >
                    {p.display_name}
                  </Checkbox>
                ))}
              </div>
            ),
          };
        })}
      />
    </>
  );

  const columns = [
    {
      title: "#",
      key: "index",
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: <b>{t("roleDisplayName")}</b>,
      dataIndex: "display_name",
      key: "display_name",
    },
    {
      title: <b>{t("roleName")}</b>,
      dataIndex: "name",
      key: "name",
      render: (name) => <Tag>{name}</Tag>,
    },
    {
      title: <b>{t("permissionsCount")}</b>,
      key: "permCount",
      render: (_, record) => (record.permissions || []).length,
    },
    {
      title: <b>{t("actions")}</b>,
      key: "actions",
      render: (_, record) => (
        <Space>
          {hasPermission("users.edit") && (
            <Button
              type="primary"
              icon={<SafetyOutlined />}
              onClick={() => openPermModal(record)}
            >
              {t("managePermissions")}
            </Button>
          )}
          {hasPermission("users.edit") && (
            <Button
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          )}
          {hasPermission("users.delete") && record.name !== "admin" && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => setDeleteTarget(record)}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="list-page">
      <Card
        title={
          <span style={{ fontWeight: "600", fontSize: "1.125rem" }}>
            {t("rolesAndPermissions")}
          </span>
        }
        variant="borderless"
      >
        {hasPermission("users.add") && (
          <Space style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateModal}
            >
              {t("addRole")}
            </Button>
          </Space>
        )}

        <Table
          loading={loading}
          dataSource={roles.map((item) => ({ ...item, key: item.id }))}
          columns={columns}
          pagination={false}
          scroll={{ x: 700 }}
        />
      </Card>

      {/* Create / Edit Role Modal */}
      <Modal
        title={editingRoleData ? t("editRole") : t("addRole")}
        open={roleModalOpen}
        onOk={handleSaveRole}
        confirmLoading={roleSaving}
        onCancel={() => {
          setRoleModalOpen(false);
          setEditingRoleData(null);
          roleForm.resetFields();
        }}
        okText={t("save")}
        cancelText={t("cancel")}
        centered
      >
        <Form form={roleForm} layout="vertical">
          <Form.Item
            name="name"
            label={t("roleName")}
            rules={[{ required: true, message: t("enterRoleName") }]}
          >
            <Input placeholder="e.g. accountant" disabled={editingRoleData?.name === "admin"} />
          </Form.Item>
          <Form.Item
            name="display_name"
            label={t("roleDisplayName")}
            rules={[{ required: true, message: t("enterRoleDisplayName") }]}
          >
            <Input placeholder="e.g. Accountant" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Permission Assignment Modal */}
      <Modal
        title={`${t("managePermissions")} - ${editingRole?.display_name || ""}`}
        open={permModalOpen}
        onOk={handlePermSave}
        confirmLoading={permSaving}
        onCancel={() => setPermModalOpen(false)}
        okText={t("save")}
        cancelText={t("cancel")}
        centered
        width={720}
      >
        <Space style={{ marginBottom: 12 }}>
          <Button size="small" onClick={selectAll}>
            {t("selectAll")}
          </Button>
          <Button size="small" onClick={deselectAll}>
            {t("deselectAll")}
          </Button>
        </Space>

        {Object.keys(featureGroups).length > 0 && (
          <>
            {renderPermissionGroup(featureGroups, t("featurePermissions"))}
            <Divider />
          </>
        )}

        {Object.keys(dataGroups).length > 0 &&
          renderPermissionGroup(dataGroups, t("dataVisibility"))}
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        title={t("confirmDelete")}
        open={Boolean(deleteTarget)}
        onOk={handleDeleteRole}
        onCancel={() => setDeleteTarget(null)}
        okText={t("yesDelete")}
        okButtonProps={{ danger: true }}
        cancelText={t("cancel")}
        centered
      >
        <p>{t("deleteRoleConfirm")}</p>
      </Modal>
    </div>
  );
};

export default GetRoles;
